import { ContextualInfoButton } from "@/components/buttons/ContextualInfoButton";
import { getBaseThemeName } from "@/features/branding";
import { getTranslatedText } from "@/features/internationalisation";
import { useAdaptationPermissions } from "@/utils/hooks/useAdaptationPermissions";
import { CaretDownIcon } from "@phosphor-icons/react/dist/ssr";
import { ConfigProvider, Select, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { CompareResultsMode } from "../types";
import type { CompareSegment, CompareSegmentState } from "../utils/compareModeState";
const COMPARE_MODE_STATE_TOOLTIP: Record<CompareSegmentState["kind"], string> = {
  ready: "",
  "no-permission": "This feature is not currently available to your organisation. Please contact your Customer Success Manager to have this feature enabled for this account.",
  loading: "Analysis data is currently loading or has not yet been prepared.",
  "needs-reprocess": `No ${getTranslatedText("terms:resilience")} data detected. Re-process this analysis to access updated data.`
};
interface CompareModeSelectorProps {
  value: CompareResultsMode;
  onChange: (value: CompareResultsMode) => void;
  label?: string;
  options: CompareSegment[];
  size?: "small" | "medium";
}
const CompareModeSelector = ({
  value,
  onChange,
  label = "Compare Resilience Pathway",
  options,
  size = "medium"
}: CompareModeSelectorProps) => {
  const {
    t
  } = useTranslation("terms");
  const {
    portfolioAutoAdaptabilityEnabled
  } = useAdaptationPermissions();
  if (getBaseThemeName() === "hkma" && !portfolioAutoAdaptabilityEnabled) return;
  const modeText = (mode: CompareResultsMode) => mode === "compare" ? "Compare" : t(`adaptationTypes.${mode}`);
  return <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1" data-sentry-component="CompareModeSelector" data-sentry-source-file="CompareModeSelector.tsx">
      <span className={size === "small" ? "text-xs" : ""}>{label}</span>
      <ConfigProvider theme={{
      components: {
        Select: {
          borderRadius: 999,
          borderRadiusSM: 999,
          activeOutlineColor: "transparent",
          controlHeightSM: 30,
          optionHeight: 26,
          optionPadding: "2px 12px"
        }
      }
    }} data-sentry-element="ConfigProvider" data-sentry-source-file="CompareModeSelector.tsx">
        <Select value={value} onChange={v => onChange(v as CompareResultsMode)} size={size === "small" ? "small" : "middle"} popupMatchSelectWidth={false} suffixIcon={<CaretDownIcon weight="bold" className="text-gray-800" />} options={options.map(segment => ({
        value: segment.mode,
        disabled: segment.disabled,
        label: modeText(segment.mode)
      }))}
      // prevent propagation from ContextualInfoButtonWithText
      optionRender={({
        value: mode
      }) => {
        const item = options.find(item => item.mode === mode);
        if (!item) return null;
        return <span className="inline-flex w-full items-center justify-between gap-1">
                <Tooltip title={COMPARE_MODE_STATE_TOOLTIP[item.state.kind]} placement="left">
                  <span>{modeText(item.mode)}</span>
                </Tooltip>
                {item.contextId && <ContextualInfoButton className="*:!text-xs" id={item.contextId} />}
              </span>;
      }} data-sentry-element="Select" data-sentry-source-file="CompareModeSelector.tsx" />
      </ConfigProvider>
    </div>;
};
export default CompareModeSelector;